import axios from "axios";
import API_URLS from "../../../config/global_variables";
import bucketsApi from "../BucketsTable/data";
import productTypesApi from "../ProductTypesTable/data";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const ERROR_MESSAGES = {
  VALIDATION_ERROR: "Please fix the highlighted fields.",
  NOT_FOUND: "Assignment not found.",
  DUPLICATE_KEY: "This product is already assigned to a bucket for this store.",
  SERVER_ERROR: "Something went wrong. Please try again.",
};

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  const code = apiErr?.code;
  const status = err?.response?.status;
  const fallback =
    apiErr?.message ||
    (status ? `Request failed (${status})` : err?.message || ERROR_MESSAGES.SERVER_ERROR);
  let message =
    (code === "VALIDATION_ERROR" && Array.isArray(apiErr?.details) && apiErr.details.length)
      ? apiErr.details.map((d) => `${d.path || "field"}: ${d.message}`).join("\n")
      : fallback || ERROR_MESSAGES[code];
  return {
    code: code || (status ? `HTTP_${status}` : "NETWORK_ERROR"),
    status,
    message,
    details: apiErr?.details || [],
  };
};

const isPositiveInt = (v) => Number.isInteger(v) && v > 0;

const validateAssignBody = (body) => {
  const errs = [];
  if (!isPositiveInt(Number(body.variant_id))) errs.push("variant_id must be a positive integer");
  if (body.bucket_required && !isPositiveInt(Number(body.bucket_id)))
    errs.push("bucket_id must be a positive integer");
  if (!body.store_hash || String(body.store_hash).length > 64)
    errs.push("store_hash is required (max 64 chars)");
  if (!body.store_label || String(body.store_label).length > 255)
    errs.push("store_label is required (max 255 chars)");
  if (![0, 1].includes(Number(body.is_default)))
    errs.push("is_default must be 0 or 1");
  if (!isPositiveInt(Number(body.prod_type_id)))
    errs.push("prod_type_id is required (a product type must be selected)");
  return errs;
};

// empty -> null; valid positive int -> that number. (Product type is required at the form level.)
const normaliseProductTypeId = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const normaliseBucketId = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const normaliseBody = ({
  variant_id,
  bucket_id,
  prod_type_id,
  store_hash,
  store_label,
  is_default,
  custom_text,
  bucket_required,
}) => ({
  variant_id: Number(variant_id),
  bucket_id: normaliseBucketId(bucket_id),
  prod_type_id: normaliseProductTypeId(prod_type_id),
  store_hash: String(store_hash || "").trim(),
  store_label: String(store_label || "").trim(),
  is_default: Number(is_default) === 1 ? 1 : 0,
  custom_text: custom_text ? String(custom_text).trim().slice(0, 255) || null : null,
  bucket_required: Boolean(bucket_required),
});

const fetchAssignment = async (variantId, storeHash) => {
  if (!isPositiveInt(Number(variantId))) throw new Error("Invalid variant_id");
  if (!storeHash) throw new Error("Missing store_hash");
  try {
    const res = await axios.get(
      `${API_URLS.PRODUCT_BUCKETS}/${encodeURIComponent(String(variantId))}`,
      { headers: headers(), params: { store_hash: storeHash } }
    );
    return res?.data?.data || null;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

const upsertAssignment = async (body) => {
  const payload = normaliseBody(body);
  const issues = validateAssignBody(payload);
  if (issues.length) {
    const e = new Error(issues.join("; "));
    e.response = {
      status: 400,
      data: {
        success: false,
        error: { code: "VALIDATION_ERROR", message: issues.join("; ") },
      },
    };
    throw e;
  }
  // bucket_required is a client-side flag; strip before sending to server
  const { bucket_required: _br, ...serverPayload } = payload;
  const res = await axios.post(`${API_URLS.PRODUCT_BUCKETS}/upsert`, serverPayload, {
    headers: headers(),
  });
  return {
    action: res?.data?.action || "saved",
    data: res?.data?.data || null,
  };
};

const listAssignments = async (params = {}) => {
  const query = {
    page: params.page || 1,
    limit: Math.min(params.limit || 20, 100),
  };
  if (params.bucket_id !== undefined) query.bucket_id = Number(params.bucket_id);
  if (params.store_hash) query.store_hash = String(params.store_hash);
  if (params.store_label) query.store_label = String(params.store_label);
  if (params.variant_id !== undefined) query.variant_id = Number(params.variant_id);
  if (params.is_default === 0 || params.is_default === 1)
    query.is_default = params.is_default;

  const res = await axios.get(API_URLS.PRODUCT_BUCKETS, {
    headers: headers(),
    params: query,
  });
  const payload = res?.data?.data || {};
  return {
    items: payload.items || [],
    pagination:
      payload.pagination || { page: 1, limit: query.limit, total: 0, totalPages: 0 },
  };
};

const fetchExistingDefaultsForStore = async (storeHash) => {
  if (!storeHash) return [];
  const PAGE_LIMIT = 100;
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const { items, pagination } = await listAssignments({
      store_hash: storeHash,
      is_default: 1,
      page,
      limit: PAGE_LIMIT,
    });
    all.push(...items);
    totalPages = Number(pagination?.totalPages) || 1;
    page += 1;
  } while (page <= totalPages && all.length < 1000);
  return all;
};

const setAsDefault = async (variantId, storeHash) => {
  if (!isPositiveInt(Number(variantId))) throw new Error("Invalid variant_id");
  if (!storeHash) throw new Error("Missing store_hash");
  const res = await axios.put(
    `${API_URLS.PRODUCT_BUCKETS}/${encodeURIComponent(String(variantId))}/default`,
    null,
    { headers: headers(), params: { store_hash: storeHash } }
  );
  return res?.data?.data || null;
};

const deleteAssignment = async (variantId, storeHash) => {
  if (!isPositiveInt(Number(variantId))) throw new Error("Invalid variant_id");
  if (!storeHash) throw new Error("Missing store_hash");
  const res = await axios.delete(
    `${API_URLS.PRODUCT_BUCKETS}/${encodeURIComponent(String(variantId))}`,
    { headers: headers(), params: { store_hash: storeHash } }
  );
  return res?.data?.data || null;
};

const fetchBucketsForStore = async (storeHash) => {
  if (!storeHash) return [];
  const PAGE_LIMIT = 100;
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const { items, pagination } = await bucketsApi.fetchBuckets({
      store_hash: storeHash,
      sort_by: "sort_order",
      sort_order: "ASC",
      page,
      limit: PAGE_LIMIT,
    });
    all.push(...items);
    totalPages = Number(pagination?.totalPages) || 1;
    page += 1;
  } while (page <= totalPages && all.length < 1000);
  return all;
};

const fetchProductTypesForStore = async (storeHash) => {
  if (!storeHash) return [];
  const PAGE_LIMIT = 100;
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const { items, pagination } = await productTypesApi.fetchProductTypes({
      store_hash: storeHash,
      is_active: 1,
      page,
      limit: PAGE_LIMIT,
    });
    all.push(...items);
    totalPages = Number(pagination?.totalPages) || 1;
    page += 1;
  } while (page <= totalPages && all.length < 1000);
  return all;
};

const apiHelpers = {
  fetchAssignment,
  upsertAssignment,
  deleteAssignment,
  fetchBucketsForStore,
  fetchProductTypesForStore,
  listAssignments,
  fetchExistingDefaultsForStore,
  setAsDefault,
  mapError,
};

export default apiHelpers;
